import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, AlertTriangle, Clock, CheckCircle2, Smartphone, Printer, QrCode } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

const BADGE = {
  awaiting_opponent: ["Awaiting opponent", "bg-white/10 text-white/70", Clock],
  disputed: ["Disputed", "bg-amber-500/20 text-amber-400", AlertTriangle],
  pending_review: ["Pending review", "bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]", Clock],
  confirmed: ["Confirmed", "bg-green-500/15 text-green-400", CheckCircle2],
  resolved: ["Resolved by organizer", "bg-green-500/15 text-green-400", CheckCircle2],
};

export function ReportBadge({ status }) {
  const b = BADGE[status];
  if (!b) return null;
  const [label, cls, Icon] = b;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid={`report-badge-${status}`}><Icon size={11} /> {label}</span>;
}

export default function ReportsPanel({ t, setT }) {
  const [items, setItems] = useState([]);
  const [zoom, setZoom] = useState("");
  const load = () => api.get(`/manage/tournaments/${t.id}/reports`).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, [t.updated_at, t.bracket?.generated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  const matches = (t.bracket?.matches || []).filter((m) => items.some((r) => r.match_id === m.id) || m.report_status);
  const order = { disputed: 0, pending_review: 1, awaiting_opponent: 2 };
  matches.sort((a, b) => (order[a.report_status] ?? 9) - (order[b.report_status] ?? 9));
  const resolve = async (mid, body) => {
    try { const r = await api.post(`/manage/tournaments/${t.id}/matches/${mid}/resolve`, body); setT(r.data); load(); toast.success(body.action === "reject" ? "Reports rejected" : "Result approved — bracket updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="reports-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 card-surface rounded-2xl p-4">
        <div className="text-sm text-white/70 flex items-center gap-2"><QrCode size={16} /> Every match has a unique QR. Captains scan it after the match to submit score + photo. Matching reports confirm automatically.</div>
        <a href={`/print/qr/${t.id}`} target="_blank" rel="noreferrer" data-testid="print-all-qr" className="inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-full bg-white text-black text-sm font-bold"><Printer size={15} /> Print all QR</a>
      </div>
      {!matches.length && <div className="card-surface rounded-2xl p-10 text-center text-white/40" data-testid="reports-empty">No score reports yet.</div>}
      {matches.map((m) => {
        const reps = items.filter((r) => r.match_id === m.id);
        const open = ["disputed", "pending_review", "awaiting_opponent"].includes(m.report_status);
        return (
          <div key={m.id} className={`card-surface rounded-2xl p-4 ${m.report_status === "disputed" ? "ring-1 ring-amber-500/40" : ""}`} data-testid={`report-match-${m.id}`}>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <div className="font-bold text-white">{m.a} <span className="text-white/30">vs</span> {m.b}</div>
              <span className="text-xs text-white/40">{m.round_name}{m.group ? ` · Group ${m.group}` : ""}</span>
              <ReportBadge status={m.report_status} />
              {open && <button onClick={() => resolve(m.id, { action: "reject" })} data-testid={`reject-reports-${m.id}`} className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-white/70 text-xs font-bold hover:text-white"><X size={12} /> Reject all & reopen</button>}
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {reps.map((r) => (
                <div key={r.id} className={`flex gap-3 rounded-xl bg-black/30 p-3 ${r.status === "rejected" ? "opacity-40" : ""}`} data-testid={`report-${r.id}`}>
                  {r.photo_url ? <button onClick={() => setZoom(r.photo_url)} className="w-24 h-16 shrink-0 rounded-lg overflow-hidden"><img src={mediaUrl(r.photo_url)} alt="proof" className="w-full h-full object-cover" data-testid={`report-thumb-${r.id}`} /></button>
                    : <div className="w-24 h-16 shrink-0 rounded-lg bg-white/5 grid place-items-center text-[10px] text-white/30">No photo</div>}
                  <div className="min-w-0 flex-1 text-xs">
                    <div className="text-white font-semibold">By {r.reporter} <span className="text-white/40 font-normal">· {r.status}</span></div>
                    <div className="text-white/80 mt-0.5">Winner: <b>{r.winner === "draw" ? "Draw" : r.winner}</b> · {r.sa} – {r.sb}</div>
                    <div className="text-white/40 mt-0.5 flex items-center gap-1" title={r.user_agent}><Smartphone size={11} /> {r.device} · {r.ip} · {new Date(r.created_at).toLocaleString()}</div>
                  </div>
                  {open && r.status === "submitted" && <button onClick={() => resolve(m.id, { report_id: r.id })} data-testid={`approve-report-${r.id}`} className="self-center inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-green-500/20 text-green-400 text-xs font-bold whitespace-nowrap"><Check size={12} /> Approve</button>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {zoom && <div className="fixed inset-0 z-[130] bg-black/90 grid place-items-center p-4" onClick={() => setZoom("")} data-testid="report-zoom"><img src={mediaUrl(zoom)} alt="proof" className="max-w-full max-h-full rounded-xl" /></div>}
    </div>
  );
}
